/* ===== Re-export shared types ===== */
import type {
  Stroke as _Stroke,
  FogReveal as _FogReveal,
  InitiativeEntry as _InitiativeEntry,
  TokenAura as _TokenAura,
} from '@roll1/shared';

export type {
  Point,
  Stroke,
  DicePart,
  DiceResult,
  ChatMessage,
  InitiativeEntry,
  FogReveal,
  SerializedToken,
  SessionState as SharedSessionState,
  SessionInfo,
  Participant,
  Role,
  FogState,
  InitiativeState,
  PageState,
  PageInfo,
  ClientMessage,
  ServerMessage,
  WsMessageBase,
  TokenAura,
  MapNote,
  MapPin,
  NoteIcon,
} from '@roll1/shared';

/* ===== Client-only Types ===== */

export type ToolName = 'select' | 'select-drawing' | 'draw' | 'erase' | 'shape' | 'measure' | 'ping' | 'fog' | 'note' | 'location-link' | 'token' | 'text' | 'aoe' | 'map-object' | 'wall' | 'inpaint-rect' | 'inpaint-lasso' | 'detail-rect';

export type ShapeType = 'rect' | 'circle' | 'line';

export interface Camera {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

export interface Token {
  id: string;
  name: string;
  x: number;
  y: number;
  size: number;
  color: string;
  imageSrc: string | null;
  imageEl: HTMLImageElement | null;
  hp: number;
  maxHp: number;
  ac: number;
  conditions: string[];
  description: string;
  notes: string;
  visible: boolean;
  characterSheetId?: string;
  auras: _TokenAura[];
  vision?: { enabled: boolean; range: number; darkvision: number; visionType?: 'normal' | 'darkvision' };
  light?: { brightRadius: number; dimRadius: number; color?: string; enabled: boolean };
  /** A player's coarse bar (0..1) for a token whose numbers they may not read (hp / maxHp are then 0); null — none. */
  hpRatio?: number | null;
}

export interface SessionState {
  tokens: Omit<Token, 'imageEl'>[];
  drawings: _Stroke[];
  fog: { enabled: boolean; revealed: _FogReveal[] };
  initiative: { entries: _InitiativeEntry[]; currentIndex: number; round: number };
  gridSize: number;
}

/** Condition IDs — language-independent keys. Use `CONDITION_KEYS` to map to i18n translation keys. */
export const CONDITIONS = [
  'blinded', 'charmed', 'deafened', 'frightened',
  'grappled', 'incapacitated', 'invisible', 'paralyzed',
  'petrified', 'poisoned', 'prone', 'restrained',
  'stunned', 'unconscious', 'exhaustion', 'concentration',
] as const;

import type { TranslationKeys } from './i18n';

/** Map condition ID to i18n translation key */
export const CONDITION_KEYS: Record<typeof CONDITIONS[number], keyof TranslationKeys> = {
  blinded: 'condition.blinded',
  charmed: 'condition.charmed',
  deafened: 'condition.deafened',
  frightened: 'condition.frightened',
  grappled: 'condition.grappled',
  incapacitated: 'condition.incapacitated',
  invisible: 'condition.invisible',
  paralyzed: 'condition.paralyzed',
  petrified: 'condition.petrified',
  poisoned: 'condition.poisoned',
  prone: 'condition.prone',
  restrained: 'condition.restrained',
  stunned: 'condition.stunned',
  unconscious: 'condition.unconscious',
  exhaustion: 'condition.exhaustion',
  concentration: 'condition.concentration',
};

/* ===== Workspace & Sidebar Types ===== */

export type WorkspaceMode = 'explore' | 'combat' | 'prep';

export type SidebarTabId =
  | 'map'
  | 'journal'
  | 'characters'
  | 'srd'
  | 'alerts'
  | 'timeline'
  /** GM only (package 4): the prepared beats, run at the table. */
  | 'plan';

/** Mapping of legacy tab IDs to valid replacements (for localStorage fallback) */
export const LEGACY_TAB_FALLBACK: Record<string, SidebarTabId> = {
  notes: 'map',
  participants: 'map',
  initiative: 'map',
  chat: 'map',
  ai: 'map',
};

export interface WorkspacePreset {
  mode: WorkspaceMode;
  defaultTool: ToolName;
  defaultTab: SidebarTabId;
  sidebarWidth: number;
}

/** @deprecated Use WorkspacePreset instead */
export type WorkspaceModeConfig = WorkspacePreset;

export interface SidebarConfig {
  width: number;
  collapsed: boolean;
  activeTab: SidebarTabId;
}

export interface UIState {
  sidebarWidth: number;
  sidebarCollapsed: boolean;
  workspaceMode: WorkspaceMode;
  activeTab: SidebarTabId;
}

/** Sidebar width (owner 25.10, «360 px»): the 48 px icon strip + a 360 px panel + the 1 px edge. */
export const SIDEBAR_DEFAULT_WIDTH = 409;
/** Widths the app used to persist as its default (320, the prep preset 400): read as «the default», so the new one applies. */
export const LEGACY_DEFAULT_WIDTHS: readonly number[] = [320, 400];
export function migrateSidebarWidth(width: number | undefined): number {
  return width === undefined || LEGACY_DEFAULT_WIDTHS.includes(width) ? SIDEBAR_DEFAULT_WIDTH : width;
}

export const DEFAULT_UI_STATE: UIState = {
  sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
  sidebarCollapsed: false,
  workspaceMode: 'explore',
  activeTab: 'map',
};

export const WORKSPACE_PRESETS: Record<WorkspaceMode, WorkspacePreset> = {
  explore: {
    mode: 'explore',
    defaultTool: 'select',
    defaultTab: 'map',
    sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
  },
  combat: {
    mode: 'combat',
    defaultTool: 'select',
    defaultTab: 'map',
    sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
  },
  prep: {
    mode: 'prep',
    defaultTool: 'draw',
    defaultTab: 'map',
    sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
  },
};

/** @deprecated Use WORKSPACE_PRESETS instead */
export const WORKSPACE_MODE_CONFIGS = WORKSPACE_PRESETS;

/* ===== Shortcut Types ===== */

export type ShortcutSection = 'tools' | 'camera' | 'tokens' | 'chat' | 'workspace';

export interface ShortcutDefinition {
  key: string;
  action: string;
  label: string;
  section: ShortcutSection;
  handler: () => void;
  when?: () => boolean;
}

/* ===== Floating Toolbar Types (043) ===== */

export interface ToolbarPosition {
  x: number;
  y: number;
}

export const DEFAULT_TOOLBAR_POSITION: ToolbarPosition = { x: 16, y: 80 };
export const TOOLBAR_STORAGE_KEY = 'toolbar-position';

export interface ToolOptionsFlyoutPassthrough {
  onFogModeChange?: (mode: string) => void;
  onToggleReFogMode?: () => void;
  onFogCoverAll?: () => void;
  onFogClearAll?: () => void;
  measurementMode?: import('@roll1/shared').MeasurementMode;
  gridSizeFt?: number;
  onMeasurementModeChange?: (mode: import('@roll1/shared').MeasurementMode) => void;
  onGridSizeFtChange?: (ft: number) => void;
  navigationMode?: 'free' | 'confirm' | 'gm-only';
  onNavigationModeChange?: (mode: string) => void;
  onAoeStartPlacement?: (
    shape: import('@roll1/shared').AoeShape,
    sizeFt: number,
    color: string,
    label?: string,
    durationMs?: number,
  ) => void;
  onAoeClearAll?: () => void;
  fogSystemMode?: 'manual' | 'dynamic';
  onFogSystemModeChange?: (mode: 'manual' | 'dynamic') => void;
  onOpenSettings?: (tab?: string) => void;
  onExpandMap?: () => void;
  onSketchToMap?: () => void;
}

export interface FloatingToolbarProps {
  isGM?: boolean;
  /** Action toggles render only when their handler is given (GM Studio has no initiative / dice panels). */
  onInitiativeToggle?: () => void;
  initiativeOpen?: boolean;
  onDiceToggle?: () => void;
  diceOpen?: boolean;
  onAiToggle?: () => void;
  aiOpen?: boolean;
  toolOptionsProps: ToolOptionsFlyoutPassthrough;
  /**
   * 'floating' (table, default): fixed to the window, draggable.
   * 'contained' (GM Studio map pane): inside the positioned map container,
   * not draggable, scrolls when the pane is short — never over the Studio
   * rail, tabs or the pinned pane.
   */
  placement?: 'floating' | 'contained';
}

export interface UseToolbarDragOptions {
  sidebarWidth: number;
  sidebarOpen: boolean;
  toolbarRef: React.RefObject<HTMLDivElement | null>;
}

export interface UseToolbarDragReturn {
  position: ToolbarPosition;
  isDragging: boolean;
  dragHandleProps: {
    onPointerDown: (e: React.PointerEvent) => void;
  };
}

export interface ToolOptionsFlyoutProps extends ToolOptionsFlyoutPassthrough {
  isGM?: boolean;
  side: 'left' | 'right';
}

/* ===== Floating Panel Types ===== */

export interface FloatingPanel {
  id: string;
  tabId: SidebarTabId;
  position: { x: number; y: number };
  size: { width: number; height: number };
}

/* ===== Mobile Types ===== */

/** Bottom sheet tab identifiers */
export type BottomSheetTab = 'ai-chat' | 'chat' | 'sheet' | 'initiative' | 'dice' | 'notes' | 'journal' | 'map' | 'timeline' | 'srd';

/** Voice input state machine */
export type VoiceInputState = 'idle' | 'listening' | 'processing' | 'error';

/** Touch gesture types (024-2) */
export type TouchGestureType =
  | 'pan'                // 1 палец по пустому месту → pan карты
  | 'pinch-zoom'         // 2 пальца → zoom
  | 'tap'                // быстрый tap → select
  | 'long-press-drag'    // 300ms на токене → drag
  | 'long-press-context'; // 500ms на токене → context menu

/* ===== Undo/Redo Types (032-A) ===== */

/** Единица отменяемого действия. Реализует Command Pattern. Frontend-only. */
export interface UndoableCommand {
  /** Уникальный тип команды для идентификации (e.g. 'token:move') */
  readonly type: string;
  /** Человекочитаемое описание для tooltip (e.g. 'Переместить Goblin') */
  readonly description: string;
  /**
   * Опциональный id сущности, которую создаёт команда (напр. token.id для
   * token:create). Позволяет снять команду со стека, если сервер отклонил
   * оптимистичную операцию (см. UndoStackAPI.dropByEntityId).
   */
  readonly entityId?: string;
  /** Выполнить действие (или повторить после undo) */
  execute(): void;
  /** Отменить действие */
  undo(): void;
}

/** Группа UndoableCommand, отменяемых/повторяемых как единое целое. */
export interface BatchCommand extends UndoableCommand {
  readonly type: 'batch';
  readonly commands: readonly UndoableCommand[];
}

/** Публичный API стека undo/redo. */
export interface UndoStackAPI {
  /** Добавить выполненную команду в стек */
  push(command: UndoableCommand): void;
  /** Отменить последнее действие */
  undo(): void;
  /** Повторить последнее отменённое действие */
  redo(): void;
  /** Очистить оба стека */
  clear(): void;
  /**
   * Снять со ОБОИХ стеков все команды, создавшие сущность с данным entityId.
   * Нужно, когда сервер отклонил оптимистичную операцию (напр. token:create):
   * призрак удалён с канваса, и «уже применённая» undo-команда должна уйти,
   * иначе Ctrl+Z отменит несуществующий объект, а Redo его воскресит.
   */
  dropByEntityId(entityId: string): void;
  /** Есть ли что отменять */
  canUndo: boolean;
  /** Есть ли что повторять */
  canRedo: boolean;
  /** Описание последнего действия для tooltip (или null) */
  undoDescription: string | null;
  /** Описание последнего отменённого действия для tooltip (или null) */
  redoDescription: string | null;
}

/** Сериализуемый snapshot токена для undo create/delete. Без imageEl (DOM). */
export type TokenSnapshot = Omit<Token, 'imageEl'>;

/** Данные для undo создания токена. */
export interface TokenCreateUndoData {
  token: TokenSnapshot;
  addToken: (token: Token) => void;
  removeToken: (id: string) => void;
  sendCreate: (token: TokenSnapshot) => void;
  sendDelete: (tokenId: string) => void;
}

/** Данные для undo удаления токена. */
export interface TokenDeleteUndoData {
  token: TokenSnapshot;
  addToken: (token: Token) => void;
  removeToken: (id: string) => void;
  sendCreate: (token: TokenSnapshot) => void;
  sendDelete: (tokenId: string) => void;
}

/** Данные для undo обновления свойств токена. */
export interface TokenUpdateUndoData {
  tokenId: string;
  tokenName: string;
  previousValues: Partial<Token>;
  newValues: Partial<Token>;
  updateToken: (id: string, updates: Partial<Token>) => void;
  sendUpdate: (tokenId: string, changes: Partial<Token>) => void;
}

/** Данные для undo перемещения рисунков. */
export interface DrawingMoveUndoData {
  strokeIds: string[];
  dx: number;
  dy: number;
  /** Apply move locally (mutate stroke coordinates) */
  applyMoveLocal: (strokeIds: string[], dx: number, dy: number) => void;
  /** Send WS drawing:move command */
  sendMove: (strokeIds: string[], dx: number, dy: number) => void;
}

/** Данные для undo удаления рисунков. */
export interface DrawingDeleteUndoData {
  strokeIds: string[];
  /** Сохранённые strokes перед удалением (deep copy) */
  deletedStrokes: _Stroke[];
  /** Send WS drawing:delete command */
  sendDelete: (strokeIds: string[]) => void;
  /** Delete strokes locally */
  deleteLocal: (strokeIds: string[]) => void;
  /** Restore strokes locally (for undo) */
  restoreLocal: (strokes: _Stroke[]) => void;
  /** Send individual drawing:stroke for each restored stroke (undo) */
  sendStroke: (stroke: _Stroke) => void;
}

/** Данные для undo рисования штриха. */
export interface DrawingStrokeUndoData {
  stroke: _Stroke;
  addStroke: (stroke: _Stroke) => void;
  removeStrokeById: (strokeId: string) => void;
  sendStroke: (stroke: _Stroke) => void;
  sendErase: (strokeId: string) => void;
}

/** Данные для undo стирания штриха. */
export interface DrawingEraseUndoData {
  stroke: _Stroke;
  addStroke: (stroke: _Stroke) => void;
  removeStrokeById: (strokeId: string) => void;
  sendStroke: (stroke: _Stroke) => void;
  sendErase: (strokeId: string) => void;
}

/** Данные для undo очистки всех рисунков. */
export interface DrawingClearUndoData {
  strokes: _Stroke[];
  setStrokes: (strokes: _Stroke[]) => void;
  clearStrokes: () => void;
  sendStroke: (stroke: _Stroke) => void;
  sendClear: () => void;
}

/** Данные для undo раскрытия тумана (batch reveal за одно drag-действие). */
export interface FogRevealUndoData {
  previousRevealed: _FogReveal[];
  newReveals: _FogReveal[];
  setRevealed: (revealed: _FogReveal[]) => void;
  sendFogReset: () => void;
  sendFogRevealBatch: (reveals: _FogReveal[]) => void;
}

/** Данные для undo сброса тумана. */
export interface FogResetUndoData {
  previousRevealed: _FogReveal[];
  wasEnabled: boolean;
  setRevealed: (revealed: _FogReveal[]) => void;
  clearRevealed: () => void;
  sendFogReset: () => void;
  sendFogRevealBatch: (reveals: _FogReveal[]) => void;
  sendFogToggle: (enabled: boolean) => void;
  setEnabled: (enabled: boolean) => void;
}

/** Данные для создания undo-команды перемещения токена. */
export interface TokenMoveUndoData {
  tokenId: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  tokenName: string;
  /** Callback для отправки WS move-команды. `undo: true` на обратном ходе —
   *  сервер ВЫЧИТАЕТ потраченное движение (values.movement_spent) вместо
   *  прибавления (этап 2 автотраты, 2026-07-08). */
  sendMove: (tokenId: string, x: number, y: number, undo?: boolean) => void;
  /** Callback для обновления локального state */
  updateToken: (tokenId: string, updates: { x: number; y: number }) => void;
}

// ── Snapshot Types (Spec 049-A) ──────────────────────────────

/** Информация о snapshot для UI */
export interface SnapshotInfo {
  id: string;
  label: string;
  timestamp: number;
}

/** Группа связанных сущностей для selective restore */
export interface RestoreGroup {
  label: string;
  entityIds: string[];
  entityType: 'sheet' | 'token' | 'note' | 'journal' | 'mixed';
  changeType: 'added' | 'modified' | 'deleted';
  checked: boolean;
}
